/**
 * Returns the appropriate email subject display text based on priority order.
 *
 * Priority order (first non-empty value wins):
 * 1. customSubject - User-defined custom subject line
 * 2. templateSubject - Subject from the selected email template
 * 3. templateName - Name of the email template (fallback)
 * 4. fallback - Custom fallback text (defaults to 'Loading...')
 *
 * @param options - Configuration object
 * @param options.customSubject - Optional custom subject line set by the user
 * @param options.templateSubject - Optional subject from the email template
 * @param options.templateName - Optional name of the email template
 * @param options.fallback - Optional custom fallback text (default: 'Loading...')
 * @returns The appropriate subject text to display
 *
 * @example
 * ```ts
 * // Returns custom subject (highest priority)
 * getEmailSubjectDisplay({
 *   customSubject: 'My Custom Subject',
 *   templateSubject: 'Template Subject',
 *   templateName: 'Newsletter'
 * }); // => 'My Custom Subject'
 *
 * // Returns template subject when custom is missing
 * getEmailSubjectDisplay({
 *   templateSubject: 'Template Subject',
 *   templateName: 'Newsletter'
 * }); // => 'Template Subject'
 *
 * // Returns template name as last resort
 * getEmailSubjectDisplay({
 *   templateName: 'Newsletter'
 * }); // => 'Newsletter'
 *
 * // Returns fallback when all are missing
 * getEmailSubjectDisplay({}); // => 'Loading...'
 *
 * // Custom fallback
 * getEmailSubjectDisplay({
 *   fallback: 'No subject available'
 * }); // => 'No subject available'
 * ```
 */
export function getEmailSubjectDisplay(options: {
	customSubject?: string;
	templateSubject?: string;
	templateName?: string;
	fallback?: string;
}): string {
	const { customSubject, templateSubject, templateName, fallback = 'Loading...' } = options;

	// Return first non-empty string, or fallback if all are empty
	return customSubject || templateSubject || templateName || fallback;
}
